import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  OnSelectChange,
  Select,
  SelectSize,
  Textarea,
} from '@ui/src';
import { AccountCurrencyTypes } from './types';
import {
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
} from '../../../../hoc/new-application/types';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from './row';

import cn from './styles.module.css';

const AccountCurrency = ({
  accountsCurrencies,
  accountCurrency,
  setAccountsCurrenciesValues,
  currencies,
  status,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  setAccountsFlowsValues,
  accountsFlowsValues,
}: AccountCurrencyTypes) => {
  const [expanded, setExpanded] = useState(false);
  const [currencyId, setCurrencyId] = useState('');
  const [accountOpeningPurpose, setAccountOpeningPurpose] = useState('');
  const [plannedTransactionNature, setPlannedTransactionNature] = useState('');
  const [currencyError, setCurrencyError] = useState('');

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const nextValues = accountsCurrencies?.map((item) => {
      const result = { ...item };

      if (result.id === accountCurrency?.id) {
        if (e.target.name === `currencyId-${accountCurrency?.id}`) {
          setCurrencyError('');
          result.currencyId = e.target.value as string;
          setCurrencyId(e.target.value as string);

          if (
            accountsCurrencies?.find(
              (accountCurrencyValue) =>
                accountCurrencyValue.currencyId === e.target.value,
            )
          ) {
            setCurrencyError('Currency must be unique');
          } else {
            let nextAccountsFlows: AccountsFlowsFormValues[] = [];

            if (accountsFlowsValues) {
              nextAccountsFlows = ([] as AccountsFlowsFormValues[]).concat(
                accountsFlowsValues,
              );
            }

            if (
              currencyId &&
              e.target.value !== currencyId &&
              nextAccountsFlows?.find((flow) => flow.currencyId === currencyId)
            ) {
              nextAccountsFlows = nextAccountsFlows?.map((flow) => {
                const flowResult = { ...flow };

                flowResult.currencyId = e.target.value as string;

                return flowResult;
              });
            } else {
              const accountsFlowsBody: AccountsFlowsFormValues[] = [
                {
                  id: `accounts-flow-${uuid()}`,
                  transactionType: 'INCOMING',
                  currencyId: e.target.value as string,
                  expectedAnnualCreditTurnover: '',
                  maxTransferAmountPerTransaction: '',
                  transactionFrequency: '',
                  transactionFrequencyPeriodMonths: '',
                },
                {
                  id: `accounts-flow-${uuid()}`,
                  transactionType: 'OUTGOING',
                  currencyId: e.target.value as string,
                  expectedAnnualCreditTurnover: '',
                  maxTransferAmountPerTransaction: '',
                  transactionFrequency: '',
                  transactionFrequencyPeriodMonths: '',
                },
              ];

              nextAccountsFlows = [...nextAccountsFlows, ...accountsFlowsBody];
            }

            setAccountsFlowsValues(nextAccountsFlows);
          }
        } else if (
          e.target.name === `accountOpeningPurpose-${accountCurrency?.id}`
        ) {
          result.accountOpeningPurpose = e.target.value as string;
          setAccountOpeningPurpose(e.target.value as string);
        } else if (
          e.target.name === `plannedTransactionNature-${accountCurrency?.id}`
        ) {
          result.plannedTransactionNature = e.target.value as string;
          setPlannedTransactionNature(e.target.value as string);
        }
      }

      return result;
    });

    setAccountsCurrenciesValues(nextValues as AccountsCurrenciesFormValues[]);
  };

  useEffect(() => {
    if (accountCurrency) {
      setCurrencyId(accountCurrency.currencyId);
      setAccountOpeningPurpose(accountCurrency.accountOpeningPurpose);
      setPlannedTransactionNature(accountCurrency.plannedTransactionNature);

      if (accountCurrency?.id?.includes('account')) {
        setExpanded(true);
      }
    }
  }, [accountCurrency]);

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {currencyId
              ? currencies?.find((item) => item.value === currencyId)?.name
              : 'Currency'}
          </button>

          {status !== CustomerStatus.CLOSED && (
            <button
              type="button"
              className={cn.deleteAccountCurrencyButton}
              onClick={() =>
                onDeleteAccountCurrencies(accountCurrency?.id as string)
              }
            >
              {deleteAccountsCurrenciesInProgress &&
              selectedAccountsCurrencyId === accountCurrency?.id ? (
                <IconLoader width={18} height={18} />
              ) : (
                <IconTrash />
              )}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className={cn.accountCurrencyContent}>
          <RegulatedCompanyRow
            title="Currency of the account"
            annualIncome
            accountsCurrencies
          >
            <Select
              name={`currencyId-${accountCurrency?.id}`}
              placeholder="Select currency..."
              list={currencies}
              value={currencyId}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={currencyError}
              borderless
              fullWidth
            />
          </RegulatedCompanyRow>
          <div className={cn.accountCurrencyFlex}>
            <div className={cn.accountCurrencyFlexItem}>
              <Textarea
                name={`accountOpeningPurpose-${accountCurrency?.id}`}
                placeholder="Describe purpose..."
                title="Purpose of the account opening"
                value={accountOpeningPurpose}
                onChange={(e) => {
                  onChange(e);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              />
            </div>
            <div className={cn.accountCurrencyFlexSeparator} />
            <div className={cn.accountCurrencyFlexItem}>
              <Textarea
                name={`plannedTransactionNature-${accountCurrency?.id}`}
                title="Nature of the planned transactions"
                placeholder="Describe purpose..."
                value={plannedTransactionNature}
                onChange={(e) => {
                  onChange(e);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountCurrency;
